import { useState, useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Progress } from '@/shared/components/ui/progress';
import { MapPin, Star, CheckCircle2, Lightbulb, ChevronDown, Phone, Mail, Calendar, Award, Users as UsersIcon, Loader2 } from 'lucide-react';
import { JourneyProgress } from './JourneyProgress';
import { ProviderMatchingSkeleton } from '@/features/resource-hub/components/ProviderMatchingSkeleton';


type ApiProvider = {
  provider_id: string;
  provider_name: string;
  overview: string;
  contact_information: string | null;
  experience: string | null;
  location: string | null;
  rating: number | null;
  consistent: null;
  availability: null;
  email: string | null;
  credentials: null;
  specialties: string[] | null;
  acceptingNewPatients: null;
  provider_category_name: string | null;
};

type ApiSpecialty = {
  specialty_id: string;
  name: string;
  what_they_do: string;
  why_you_need_them: string;
  providers: ApiProvider[];
};

interface Provider {
  id: string;
  name: string;
  specialty: string;
  provider_category_name?: string;
  location: string;
  rating: number | null;
  bio: string;
  avatar?: string;
  experience?: string;
  availability?: string;
  phone?: string;
  email?: string;
  credentials?: string[];
  specialties?: string[];
  acceptingNewPatients?: boolean;
}

interface ProviderCategory {
  specialty_id: string;
  title: string;
  description: string;
  whatTheyDo: string;
  howToAssess: string[];
  providers: Provider[];
}

interface ProviderMatchingState {
  sessionId?: string;
}


export function ProviderMatching() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = (location.state as ProviderMatchingState | null)?.sessionId;
  const { categoryIndex } = useParams();
  const currentIndex = parseInt(categoryIndex || '0');
  // ── Persist selections in localStorage so page refresh restores them ─────────
  // Keyed by specialty_id (not index) since visibleCategories' indices shift once
  // isAllDone flips and zero-provider specialties are added back into the list.
  const selectionsKey = sessionId ? `fp-selections-${sessionId}` : null;
  const [selectedProviders, setSelectedProviders] = useState<Map<string, Set<string>>>(() => {
    if (!selectionsKey) return new Map();
    try {
      const saved = localStorage.getItem(selectionsKey);
      if (!saved) return new Map();
      const parsed = JSON.parse(saved) as [string, string[]][];
      return new Map(parsed.map(([k, v]) => [k, new Set(v)]));
    } catch {
      return new Map();
    }
  });

  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [expandedBios] = useState<Set<string>>(new Set());
  const [isBuildingPlan, setIsBuildingPlan] = useState(false);

  // ── SSE: backend pushes SPECIALTY_READY after each cycle, READY when all done
  const [isReady, setIsReady] = useState(false);
  const [isAllDone, setIsAllDone] = useState(false);
  // How many specialties Phase 2 has finished so far. Backend processes specialties
  // strictly in order (1st, then 2nd, then 3rd...), so this count tells us exactly
  // how many leading entries in `categories` are done — used instead of filtering by
  // providers.length > 0, which broke order whenever an earlier specialty legitimately
  // had zero matches (NO_RESULTS) and a later one finished with results first.
  const [completedCount, setCompletedCount] = useState(0);
  // ── API 1: Specialty names from Stage 3 DB — instant, no pipeline needed ─────
  const { data: specialtyTypes = [] } = useQuery<{ specialty_id: string; name: string }[]>({
    queryKey: ['find-providers-specialty-types', sessionId],
    enabled: !!sessionId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data } = await apiClient.get<{ specialty_id: string; name: string }[]>(
        '/find-providers/specialty-types',
        { params: { session_id: sessionId } },
      );
      return data;
    },
  });
  // Tracks whether the query has been enabled yet — avoids double-fetch on first signal
  const isReadyRef = useRef(false);

  // ── Fetch specialties — stable queryKey so refetch never resets the screen ──
  const { data: categories = [], isPending: isLoadingCategories, refetch } = useQuery<ProviderCategory[]>({
    queryKey: ['find-providers-specialties', sessionId],
    enabled: !!sessionId && isReady,
    staleTime: Infinity,        // keep fetched data in cache — no re-fetch on re-navigation
    refetchOnWindowFocus: false, // prevent extra calls when user clicks around
    refetchOnMount: false,       // re-navigation uses cache, not a new fetch
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSpecialty[]>('/find-providers/specialties', {
        params: { session_id: sessionId },
      });
      return data.map((specialty) => ({
        specialty_id: specialty.specialty_id,
        title: specialty.name,
        description: specialty.why_you_need_them,
        whatTheyDo: specialty.what_they_do,
        howToAssess: [],
        providers: specialty.providers.map((p) => ({
          id: p.provider_id,
          name: p.provider_name,
          specialty: specialty.name,
          provider_category_name: p.provider_category_name ?? undefined,
          location: p.location ?? '',
          rating: p.rating ?? null,
          bio: p.overview,
          phone: p.contact_information ?? undefined,
          experience: p.experience ?? undefined,
          email: p.email ?? undefined,
          credentials: p.credentials ?? undefined,
          specialties: p.specialties ?? undefined,
          availability: p.availability ?? undefined,
          acceptingNewPatients: p.acceptingNewPatients ?? undefined,
        })),
      }));
    },
  });

  // Ref so SSE handler always calls the latest refetch without re-opening the connection
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!sessionId) return;

    let sseSource: EventSource | null = null;

    const openSse = () => {
      const base = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api/v1';
      const url = `${base}/find-providers/stream?session_id=${encodeURIComponent(sessionId)}`;
      sseSource = new EventSource(url);

      sseSource.onmessage = (e: MessageEvent) => {
        const msg = JSON.parse(e.data as string) as { status: string };
        if (msg.status === 'SPECIALTY_READY') {
            setCompletedCount((c) => c + 1);
          if (!isReadyRef.current) {
            // First specialty: enabling the query auto-fetches — no explicit refetch needed
            isReadyRef.current = true;
            setIsReady(true);
          } else {
            // 2nd, 3rd, 4th...: query already enabled, explicit refetch to get new data
            void refetchRef.current();
          }
        } else if (msg.status === 'READY') {
          // All specialties done — mark complete and do a final fetch for any missed specialties
          setIsAllDone(true);
          if (!isReadyRef.current) {
            isReadyRef.current = true;
            setIsReady(true);
          } else {
            void refetchRef.current();
          }
          sseSource?.close();
        }
      };

      sseSource.onerror = () => {
        if (!isReadyRef.current) {
          isReadyRef.current = true;
          setIsReady(true);
        }
        sseSource?.close();
      };
    };

    // Check status first — if already READY (user navigated back), skip SSE entirely
    // and fetch directly. Only open SSE when pipeline is still in progress.
    void apiClient
      .get<{ status: string }>('/find-providers/status', { params: { session_id: sessionId } })
      .then(({ data }) => {
        if (data.status === 'READY') {
          isReadyRef.current = true;
          setIsAllDone(true);
          setIsReady(true); // query uses cache immediately — no SSE needed
        } else {
          openSse(); // pipeline still running — listen for events
        }
      })
      .catch(() => openSse()); // status check failed — fall back to SSE

    return () => sseSource?.close();
  }, [sessionId]);

  // Save selections to localStorage whenever they change
  useEffect(() => {
    if (!selectionsKey) return;
    const toSave = [...selectedProviders.entries()].map(([k, v]) => [k, [...v]]);
    localStorage.setItem(selectionsKey, JSON.stringify(toSave));
  }, [selectedProviders, selectionsKey]);

   // During pipeline: show specialties strictly in backend processing order — the first
  // `completedCount` entries in `categories`, since Phase 2 finishes them sequentially.
  // After all done: show all specialties including those with no providers.
  const visibleCategories = isAllDone
    ? categories
    : categories.slice(0, completedCount);

  const category = visibleCategories[currentIndex];
  const progress = ((currentIndex + 1) / Math.max(visibleCategories.length, 1)) * 100;
  const isLastStep = visibleCategories.length > 0 && currentIndex === visibleCategories.length - 1;

  const isSelected = (providerId: string) =>
    selectedProviders.get(category?.specialty_id ?? '')?.has(providerId) ?? false;

  const handleSelectProvider = (providerId: string) => {
    const specialtyId = category?.specialty_id;
    if (!specialtyId) return;
    setSelectedProviders((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(specialtyId) ?? []);
      if (set.has(providerId)) {
        set.delete(providerId);
      } else {
        set.add(providerId);
      }
      next.set(specialtyId, set);
      return next;
    });
  };

  const buildCarePlanWithSelections = async (selections: Map<string, Set<string>>) => {
    if (!sessionId) {
      navigate({ pathname: '/detailed-care-navigator/plan' }, { state: { sessionId } });
      return;
    }
    setIsBuildingPlan(true);
    const selectedList: { specialty_id: string; provider_ids: string[] }[] = [];
    for (const [specialtyId, providerIds] of selections.entries()) {
      if (providerIds.size === 0) continue;
      selectedList.push({
        specialty_id: specialtyId,
        provider_ids: Array.from(providerIds),
      });
    }
    try {
      await apiClient.post('/find-providers/care-plan', {
        session_id: sessionId,
        selected_providers: selectedList,
      });
    } catch {
      // proceed even on error — CarePlan page will show whatever is in DB
    } finally {
      setIsBuildingPlan(false);
    }
    navigate({ pathname: '/detailed-care-navigator/plan' }, { state: { sessionId } });
  };

  const buildCarePlanAndNavigate = async () => buildCarePlanWithSelections(selectedProviders);

  const handleContinue = async () => {
    if (currentIndex < visibleCategories.length - 1) {
      navigate({ pathname: `/detailed-care-navigator/matching/${currentIndex + 1}` }, { state: { sessionId } });
    } else {
      await buildCarePlanAndNavigate();
    }
  };

  const handleSkip = async () => {
    if (currentIndex < visibleCategories.length - 1) {
      navigate({ pathname: `/detailed-care-navigator/matching/${currentIndex + 1}` }, { state: { sessionId } });
    } else {
      await buildCarePlanAndNavigate();
    }
  };

  const handleUseTopRecommendation = async () => {
    const topProvider = category?.providers[0];
    if (!topProvider || !category) return;
    const updatedSelections = new Map(selectedProviders);
    updatedSelections.set(category.specialty_id, new Set([topProvider.id]));
    setSelectedProviders(updatedSelections);
    if (currentIndex < visibleCategories.length - 1) {
      navigate({ pathname: `/detailed-care-navigator/matching/${currentIndex + 1}` }, { state: { sessionId } });
    } else {
      await buildCarePlanWithSelections(updatedSelections);
    }
  };

  // Show skeleton while:
  // 1. Haven't heard from backend yet (isReady = false)
  // 2. OR the current specialty isn't loaded yet (pipeline still running for it)
  if (!isReady || isLoadingCategories || (!category && !isAllDone)) {
    return (
      <div className="min-h-screen">
        <JourneyProgress currentStep="matching" />
        <ProviderMatchingSkeleton />
      </div>
    );
  }

  if (!category) {
    return <Navigate to="/detailed-care-navigator/transition" replace />;
  }


  return (
    <div className="min-h-screen bg-background">
      <JourneyProgress currentStep="matching" />

      <div className="w-full px-6 py-8">
        <div className="max-w-[1400px] mx-auto">

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">
                Provider Type {currentIndex + 1} of {visibleCategories.length}
              </span>
              <span className="text-sm font-semibold text-[#3d7872]">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <Progress value={progress} className="h-2 bg-gray-200 [&>div]:bg-[#3d7872]" />
          </div>

          {/* Provider Types Navigation Card */}
          <Card className="p-6 mb-8">
            <h2 className="text-base font-bold text-gray-900 mb-1">Provider Types in Your Care Plan</h2>
            <p className="text-sm text-gray-500 mb-5">
              Jump to a specific provider type or continue through the matching process.
            </p>
            <div className="flex flex-wrap gap-3 mb-5">
              {/* Use specialtyTypes (Stage 3 DB) for nav pills — loads instantly on mount.
                  Falls back to visibleCategories if specialty-types API not yet responded. */}
              {(specialtyTypes.length > 0 ? specialtyTypes : visibleCategories).map((item) => {
                const title = 'title' in item ? item.title : item.name;
                const isLoaded = visibleCategories.some((c) => c.specialty_id === item.specialty_id);
                return (
                  <button
                    key={item.specialty_id}
                    onClick={() => {
                      if (isLoaded) {
                        const loadedIdx = visibleCategories.findIndex((c) => c.specialty_id === item.specialty_id);
                        navigate({ pathname: `/detailed-care-navigator/matching/${loadedIdx}` }, { state: { sessionId } });
                      }
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      visibleCategories[currentIndex]?.specialty_id === item.specialty_id
                        ? 'bg-[#3d7872] text-white'
                        : isLoaded
                          ? 'border border-gray-300 text-gray-700 hover:border-[#3d7872] hover:text-[#3d7872]'
                          : 'border border-gray-200 text-gray-400 cursor-default'
                    }`}
                  >
                    {title}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <Button
                size="sm"
                className="bg-[#3d7872] hover:bg-[#2d6862] text-white rounded-xl gap-1.5"
                onClick={handleUseTopRecommendation}
                disabled={!category?.providers.length}
              >
                {isLastStep ? 'Use Top Recommendation & Build Plan →' : 'Use Top Recommendation →'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-gray-300 text-gray-700 rounded-xl hover:border-gray-400"
                onClick={handleSkip}
              >
                {isLastStep ? 'Skip & Build My Care Plan' : 'Skip This Type'}
              </Button>
            </div>
          </Card>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

            {/* Left Column — Category Info */}
            <div className="space-y-5 lg:sticky lg:top-8 lg:self-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-3">{category.title}</h1>
                <p className="text-gray-600 leading-relaxed">{category.description}</p>
              </div>
              <Card className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <Lightbulb className="text-[#3d7872] flex-shrink-0 mt-0.5" size={20} />
                  <h2 className="font-semibold text-gray-900">What They Do</h2>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{category.whatTheyDo}</p>
              </Card>
            </div>

            {/* Right Column — Recommendations */}
            <div>
              <div className="mb-5">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Our Recommendations</h2>
                <p className="text-sm text-gray-500">
                  Based on your location and needs, here are our top matches:
                </p>
              </div>

              <div className="space-y-5 mb-6">
                {category.providers.length === 0 && (
                  <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                    <p className="text-gray-600 font-medium">No providers found for this specialty in your area.</p>
                    <p className="text-sm text-gray-500 mt-2">You can skip this step and continue building your care plan.</p>
                  </div>
                )}

                {category.providers.map((provider, index) => (
                  <div
                    key={provider.id}
                    onClick={() => handleSelectProvider(provider.id)}
                    className={`relative rounded-xl border transition-all cursor-pointer ${
                      isSelected(provider.id)
                        ? 'bg-[#3d7872]/5 border-[#3d7872] ring-1 ring-[#3d7872] shadow-sm'
                        : 'bg-white border-gray-200 hover:border-[#3d7872]/50 hover:shadow-sm'
                    }`}
                  >
                    {/* Top Match badge */}
                    {index === 0 && (
                      <div className="absolute -top-3 left-4">
                        <span className="bg-[#3d7872] text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Top Match
                        </span>
                      </div>
                    )}

                    <div className={`p-5 ${index === 0 ? 'pt-7' : ''}`}>
                      <div className="flex gap-4">
                        {/* Avatar */}
                        <div className="w-14 h-14 bg-gradient-to-br from-[#3d7872] to-[#2d6862] rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0">
                          {provider.name.split(' ').slice(0, 3).map(n => n[0]).join('')}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-1.5">
                            <div>
                              <h3 className="text-base font-bold text-gray-900 mb-1.5">
                                {provider.name}
                              </h3>
                              <Badge variant="secondary" className="bg-teal-50 text-[#3d7872] border-none text-xs font-medium">
                                {provider.provider_category_name ?? provider.specialty}
                              </Badge>
                            </div>
                            {isSelected(provider.id) && (
                              <CheckCircle2 className="text-[#3d7872] flex-shrink-0" size={22} />
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-3 mt-2.5 mb-3 text-sm text-gray-600">
                            {provider.location && (
                              <div className="flex items-center gap-1">
                                <MapPin size={13} />
                                <span>{provider.location}</span>
                              </div>
                            )}
                            {provider.rating !== null && (
                              <div className="flex items-center gap-1">
                                <Star size={13} className="fill-yellow-400 text-yellow-400" />
                                <span className="font-medium">{provider.rating.toFixed(1)}</span>
                              </div>
                            )}
                            {provider.availability && (
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  provider.acceptingNewPatients
                                    ? 'border-green-500 text-green-700'
                                    : 'border-amber-500 text-amber-700'
                                }`}
                              >
                                {provider.availability}
                              </Badge>
                            )}
                          </div>

                          <p className={`text-sm text-gray-700 leading-relaxed mb-4 ${expandedBios.has(provider.id) ? '' : 'line-clamp-3'}`}>
                            {provider.bio}
                          </p>

                          <div className="flex items-center gap-3 flex-wrap">
                            <Button
                              variant={isSelected(provider.id) ? 'default' : 'outline'}
                              size="sm"
                              className={`rounded-lg ${
                                isSelected(provider.id)
                                  ? 'bg-[#3d7872] hover:bg-[#2d6862] text-white'
                                  : 'border-[#3d7872] text-[#3d7872] hover:bg-[#3d7872] hover:text-white'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectProvider(provider.id);
                              }}
                            >
                              {isSelected(provider.id) ? 'Selected' : 'Select Provider'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-gray-300 text-gray-700 rounded-lg hover:border-gray-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedProvider(expandedProvider === provider.id ? null : provider.id);
                              }}
                            >
                              {expandedProvider === provider.id ? 'Less Info' : 'More Info'}
                              <ChevronDown
                                className={`ml-1.5 h-3.5 w-3.5 transition-transform ${
                                  expandedProvider === provider.id ? 'rotate-180' : ''
                                }`}
                              />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Details */}
                    {expandedProvider === provider.id && (
                      <div className="px-5 pb-5 border-t border-gray-100">
                        <div className="grid md:grid-cols-2 gap-6 mt-5">
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Phone size={15} className="text-[#3d7872]" />
                                Contact Information
                              </h4>
                              {provider.phone || provider.email ? (
                                <div className="space-y-2 text-sm text-gray-700">
                                  {provider.phone && (
                                    <div className="flex items-center gap-2">
                                      <Phone size={13} className="text-gray-400" />
                                      <a href={`tel:${provider.phone}`} className="hover:text-[#3d7872]">{provider.phone}</a>
                                    </div>
                                  )}
                                  {provider.email && (
                                    <div className="flex items-center gap-2">
                                      <Mail size={13} className="text-gray-400" />
                                      <a href={`mailto:${provider.email}`} className="hover:text-[#3d7872]">{provider.email}</a>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic">None</p>
                              )}
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <Calendar size={15} className="text-[#3d7872]" />
                                Experience
                              </h4>
                              {provider.experience ? (
                                <p className="text-sm text-gray-700">{provider.experience}</p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">None</p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Award size={15} className="text-[#3d7872]" />
                                Credentials
                              </h4>
                              {provider.credentials && provider.credentials.length > 0 ? (
                                <ul className="space-y-1">
                                  {provider.credentials.map((cred, idx) => (
                                    <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                      <span className="text-[#3d7872] mt-1">•</span>
                                      <span>{cred}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-gray-400 italic">None</p>
                              )}
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <UsersIcon size={15} className="text-[#3d7872]" />
                                Areas of Focus
                              </h4>
                              {provider.specialties && provider.specialties.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {provider.specialties.map((spec, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs border-[#3d7872]/30 text-gray-700">
                                      {spec}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic">None</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleContinue}
                  disabled={isBuildingPlan}
                  className="w-full h-14 bg-[#3d7872] hover:bg-[#2d6862] text-white text-base font-semibold rounded-xl disabled:opacity-50"
                >
                  {isBuildingPlan ? (
                    <><Loader2 size={18} className="mr-2 animate-spin" />Building your plan...</>
                  ) : currentIndex < visibleCategories.length - 1 ? (
                    'Continue to Next Provider Type'
                  ) : (
                    'Build My Care Plan'
                  )}
                </Button>
                <Button
                  onClick={handleSkip}
                  className="w-full h-14 bg-gray-100 hover:bg-gray-200 text-gray-500 text-base font-semibold rounded-xl"
                  variant="ghost"
                >
                  {isLastStep ? 'Skip & Build My Care Plan' : 'Skip This Provider Type'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
